import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Phone, ShieldCheck } from 'lucide-react';
import { PD_POLICY_VERSION, type ProfileResponse } from '@masha/shared';
import { api, ApiError } from '../lib/api';
import { haptic, openExternalLink, requestPhoneContact } from '../lib/tma';
import { Button, Card } from './ui';

type Problem = 'cancelled' | 'unsupported' | 'failed';

const PROBLEM_TEXT: Record<Problem, string> = {
  cancelled: 'Без номера продолжить нельзя. Его увидит только команда Маши.',
  unsupported: 'Обнови Telegram, чтобы продолжить: эта версия не умеет делиться номером.',
  failed: 'Не получилось. Попробуй ещё раз.',
};

/**
 * Gate screen: one tap shares the phone (native Telegram popup) and accepts the
 * personal-data policy. Users who already shared a phone only re-accept a new
 * policy version.
 */
export function ConsentScreen({ hasPhone }: { hasPhone: boolean }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  async function submit() {
    setBusy(true);
    setProblem(null);
    try {
      let contact: string | undefined;
      if (!hasPhone) {
        const result = await requestPhoneContact();
        if (result.kind !== 'sent') {
          setProblem(result.kind === 'no_data' ? 'failed' : result.kind);
          haptic('warning');
          return;
        }
        contact = result.response;
      }
      const profile = await api.acceptConsent({ policyVersion: PD_POLICY_VERSION, contact });
      haptic('success');
      queryClient.setQueryData<ProfileResponse>(['profile'], profile);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'POLICY_OUTDATED') {
        // The bundle carries an old policy version — reload to get the new text.
        window.location.reload();
        return;
      }
      setProblem('failed');
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  const policyUrl = `${window.location.origin}/privacy.html`;

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <Card variant="elevated" padding="lg" className="w-full">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-muted text-brand mb-4">
          {hasPhone ? <ShieldCheck size={32} /> : <Phone size={32} />}
        </div>
        <h1 className="text-xl font-bold mb-2">
          {hasPhone ? 'Мы обновили политику' : 'Последний шаг'}
        </h1>
        <p className="text-sm text-tg-hint mb-6">
          {hasPhone
            ? 'Прочитай обновлённую политику обработки персональных данных и подтверди согласие.'
            : 'Поделись номером телефона — так команда Маши сможет с тобой связаться, а бонусы будут защищены от накруток.'}
        </p>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={busy}
          leftIcon={hasPhone ? undefined : <Phone size={18} />}
          onClick={submit}
        >
          {hasPhone ? 'Принимаю' : 'Поделиться номером'}
        </Button>
        {problem && <p className="text-sm text-destructive mt-3">{PROBLEM_TEXT[problem]}</p>}
        <p className="text-xs text-tg-hint mt-4">
          Нажимая кнопку, ты соглашаешься с{' '}
          <button
            type="button"
            className="text-tg-link underline"
            onClick={() => openExternalLink(policyUrl)}
          >
            Политикой обработки персональных данных
          </button>{' '}
          и даёшь согласие на обработку номера телефона и данных Telegram, в том числе на звонки и
          сообщения от команды Маши.
        </p>
      </Card>
    </div>
  );
}
